// GameHistory.tsx
import React, { FC, useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import { GithubFormat } from '../../utils/formatCode';
import Tooltip from '../../../../ui/components/Tooltip';
import Button from '../../../../ui/components/Button';
import ButtonMicro from '../../../../ui/components/ButtonMicro';
import MenuOpen from '../../../../ui/components/MenuOpen';
import { GameHistoryEntry } from '../../../../backend/useGame';
import { useConfig } from '../../../../utils/config';

const HistoryContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    z-index: 100;
`;

const CopyButtonWrapper = styled.div<{ $isVisible: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${props => props.$isVisible ? 0.7 : 0};
`;

interface GameHistoryProps {
    history: Partial<GameHistoryEntry>[];
    onHistorySelect: (entry: Partial<GameHistoryEntry>) => void;
    selectedHistoryId: string;
}

// Utility function to copy text to clipboard
const copyTextToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text.trim());
        // console.log('Text copied to clipboard');
    } catch (err) {
        console.error('Failed to copy text: ', err);
    }
};

const GameHistory: FC<GameHistoryProps> = ({ history, onHistorySelect, selectedHistoryId }) => {
    const config = useConfig();
    const historyButtonRef = useRef<HTMLDivElement>(null);
    const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
    const [historyMenuOpen, setHistoryMenuOpen] = useState(false);
    const [inputWidth, setInputWidth] = useState('100%');
    const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);

    const handleHistoryClick = () => {
        // console.log('history clicked');
        if (historyButtonRef.current) {
            setHistoryAnchorEl(historyButtonRef.current);
            setHistoryMenuOpen(!historyMenuOpen);
            
            // Get the width of the GameInput container
            const gameInputContainer = document.querySelector('.instruction-container');
            if (gameInputContainer) {
                const width = gameInputContainer.getBoundingClientRect().width;
                setInputWidth(`${width}px`);
            }
        }
    };

    const handleHistoryClose = () => {
        setHistoryMenuOpen(false);
        setHistoryAnchorEl(null);
        setHoveredItemIndex(null); // Reset hovered item when menu closes
    };

    const handleSelectHistoryEntry = (entry: Partial<GameHistoryEntry>) => {
        onHistorySelect(entry);
        handleHistoryClose();
    };

    const handleMenuMouseLeave = () => {
        setHoveredItemIndex(null);
    };

    // Auto-scroll to bottom of history menu when it opens
    useEffect(() => {
        if (historyMenuOpen) {
            // Use setTimeout to ensure the menu is rendered before scrolling
            setTimeout(() => {
                // The StyledMenuList is inside the StyledPaper with id='paper'
                const menuListElement = document.querySelector('#paper .MuiList-root');
                // console.log('Menu list element:', menuListElement);
                if (menuListElement) {
                    menuListElement.scrollTop = menuListElement.scrollHeight;
                }
            }, 100);
        }
    }, [historyMenuOpen]);

    // Memoized history options that update when history changes
    const historyOptions = useMemo(() => {
        const mapEntryToOption = (entry: Partial<GameHistoryEntry>, index: number, handleSelectHistoryEntry: (entry: Partial<GameHistoryEntry>) => void) => {
            const textSub = entry.prompt?.startsWith('EDIT: ') ? entry.prompt.slice(6) : entry.prompt;
            return {
                text: index + 1 + ': ',
                text_sub: textSub,
                icon: entry.status === "ERROR" ? 'warning' : entry.status === "IN_PROGRESS" ? 'loading' : entry.status === "SUCCESS" || entry.status === "CANCELLED" ? 'undo' : '',
                disabled: entry.status === "ERROR" || (entry.status === "IN_PROGRESS" && !config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES),
                error: entry.status === "ERROR",
                onClick: entry.status === "ERROR" || (entry.status === "IN_PROGRESS" && !config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES) ? undefined : () => handleSelectHistoryEntry(entry),
                isSelected: entry.id === selectedHistoryId,
                onHover: () => setHoveredItemIndex(index),
                onMouseLeave: () => setHoveredItemIndex(null), // Add onMouseLeave for each item
                rightChildren: (
                    <CopyButtonWrapper $isVisible={hoveredItemIndex === index}>
                        <ButtonMicro
                            icon="copy"
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent the menu item's onClick from firing
                                if (textSub) {
                                    copyTextToClipboard(textSub);
                                }
                            }}
                            colorMode="dark"
                            size={14}
                            padding="4px"
                            showBorder={false}
                        />
                    </CopyButtonWrapper>
                ),
            };
        };
        
        // If DEBUG_MODE is true, include all history options
        if (config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES) {
            return history.map((entry, index) => mapEntryToOption(entry, index, handleSelectHistoryEntry));
        }
        
        // Normal mode
        const sortedHistory = [...history].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const mostRecentInProgress = sortedHistory.find(item => item.status === "IN_PROGRESS");
        const mostRecentEntry = sortedHistory[0]; // Most recent entry of any type
        
        const options = history
            .filter((item) => {
                // Include all SUCCESS items
                if (item.status === "SUCCESS") return true;
                // Include CANCELLED items
                if ((item as any).status === "CANCELLED") return true;
                // Include all ERROR items
                if (item.status === "ERROR") return true;
                // Include the most recent IN_PROGRESS item if it's also the most recent entry of any type
                if (item.status === "IN_PROGRESS" && item.id === mostRecentInProgress?.id && item.id === mostRecentEntry?.id) {
                    return true;
                }
                // Exclude all other items
                return false;
            })
            .map((entry, index) => mapEntryToOption(entry, index, handleSelectHistoryEntry));
        
        // console.log('History options:', options);
        return options;
    }, [history, selectedHistoryId, hoveredItemIndex]);

    const hasValidHistoryOptions = historyOptions?.length > 0;

    return (
        <HistoryContainer id="history-container" ref={historyButtonRef}>
            <Tooltip text="History" colorMode="dark" hide={historyMenuOpen}>
                <Button
                    size="tiny"
                    color="ghost white"
                    icon="history"
                    onClick={handleHistoryClick}
                    disabled={!hasValidHistoryOptions}
                />
            </Tooltip>
            {hasValidHistoryOptions && (
                <MenuOpen
                    anchorEl={historyAnchorEl}
                    open={historyMenuOpen}
                    onClose={handleHistoryClose}
                    options={historyOptions}
                    color="black"
                    minWidth={10}
                    maxWidth={inputWidth}
                    placement="top-start"
                    onMenuLeave={handleMenuMouseLeave} // Pass the handler to MenuOpen
                />
            )}
        </HistoryContainer>
    );
};

export default GameHistory;